/**
 * Cosmetic catalogue: avatars, avatar frames and name styles.
 * Shared so the shop, profiles and the server's ownership checks agree.
 */

export type CosmeticKind = 'avatar' | 'frame' | 'nameStyle';
export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';
/** starter: everyone owns it · shop: bought with gold · box: only from gift boxes. */
export type CosmeticSource = 'starter' | 'shop' | 'box';

export type AvatarTone =
  | 'sand'
  | 'sky'
  | 'blush'
  | 'meadow'
  | 'forest'
  | 'frost'
  | 'ocean'
  | 'stone'
  | 'night'
  | 'abyss'
  | 'ember'
  | 'gold'
  | 'royal';

export interface Cosmetic {
  id: string;
  kind: CosmeticKind;
  rarity: Rarity;
  source: CosmeticSource;
  /** Gold price in the shop; null when it cannot be bought with gold. */
  price: number | null;
  /** Rewarded ads that unlock it instead of gold; absent when ads cannot unlock it. */
  ads?: number;
  /** Display name in both languages. */
  name: { tr: string; en: string };
  /** Avatars: the emoji shown. Frames / name styles: rendered by CSS class `frame-<id>` / `name-<id>`. */
  emoji?: string;
  /** Avatars: background medallion (CSS class `tone-<tone>`). */
  tone?: AvatarTone;
  /** Optional symbol placed around a decorated name. */
  flair?: string;
}

type Name = [tr: string, en: string];

const avatar = (
  id: string,
  emoji: string,
  tone: AvatarTone,
  [tr, en]: Name,
  source: CosmeticSource,
  rarity: Rarity,
  price: number | null = null,
): Cosmetic => ({ id: `avatar.${id}`, kind: 'avatar', emoji, tone, name: { tr, en }, source, rarity, price });

const frame = (id: string, [tr, en]: Name, source: CosmeticSource, rarity: Rarity, price: number | null = null): Cosmetic => ({
  id: `frame.${id}`,
  kind: 'frame',
  name: { tr, en },
  source,
  rarity,
  price,
});

const nameStyle = (
  id: string,
  [tr, en]: Name,
  source: CosmeticSource,
  rarity: Rarity,
  price: number | null,
  flair?: string,
): Cosmetic => ({
  id: `name.${id}`,
  kind: 'nameStyle',
  name: { tr, en },
  source,
  rarity,
  price,
  ...(flair ? { flair } : {}),
});

/**
 * Ids never change once released: players own them. An item can be renamed and
 * restyled freely (the old "rainbow" items are now Dragonscale and Dragonfire).
 */
const CATALOGUE: readonly Cosmetic[] = [
  // Starter avatars: free for everyone.
  avatar('cat', '🐱', 'sand', ['Kedi', 'Cat'], 'starter', 'common'),
  avatar('dog', '🐶', 'sky', ['Köpek', 'Dog'], 'starter', 'common'),
  avatar('rabbit', '🐰', 'blush', ['Tavşan', 'Rabbit'], 'starter', 'common'),
  avatar('fox', '🦊', 'forest', ['Tilki', 'Fox'], 'starter', 'common'),
  avatar('panda', '🐼', 'frost', ['Panda', 'Panda'], 'starter', 'common'),
  avatar('frog', '🐸', 'meadow', ['Kurbağa', 'Frog'], 'starter', 'common'),
  avatar('penguin', '🐧', 'ocean', ['Penguen', 'Penguin'], 'starter', 'common'),
  avatar('koala', '🐨', 'stone', ['Koala', 'Koala'], 'starter', 'common'),

  // Shop avatars: creatures, heroes and legends.
  avatar('chick', '🐥', 'sand', ['Civciv', 'Chick'], 'shop', 'common', 150),
  avatar('turtle', '🐢', 'ocean', ['Bilge Kaplumbağa', 'Sage Turtle'], 'shop', 'common', 150),
  avatar('owl', '🦉', 'night', ['Gece Baykuşu', 'Night Owl'], 'shop', 'rare', 250),
  avatar('octopus', '🐙', 'abyss', ['Kraken', 'Kraken'], 'shop', 'rare', 250),
  avatar('dolphin', '🐬', 'ocean', ['Yunus', 'Dolphin'], 'shop', 'rare', 300),
  avatar('wolf', '🐺', 'night', ['Ay Kurdu', 'Moon Wolf'], 'shop', 'rare', 300),
  avatar('robot', '🤖', 'frost', ['Mekanik Golem', 'Automaton'], 'shop', 'rare', 300),
  avatar('fairy', '🧚', 'blush', ['Orman Perisi', 'Forest Fairy'], 'shop', 'rare', 320),
  avatar('alien', '👽', 'night', ['Yıldız Gezgini', 'Star Wanderer'], 'shop', 'rare', 320),
  avatar('lion', '🦁', 'gold', ['Aslan Yürek', 'Lionheart'], 'shop', 'rare', 350),
  avatar('elf', '🧝', 'forest', ['Elf Okçu', 'Elven Archer'], 'shop', 'rare', 350),
  avatar('ninja', '🥷', 'stone', ['Gölge Ninja', 'Shadow Ninja'], 'shop', 'rare', 350),
  avatar('tiger', '🐯', 'ember', ['Savaş Kaplanı', 'War Tiger'], 'shop', 'epic', 450),
  avatar('trex', '🦖', 'forest', ['Kadim Kral', 'Tyrant King'], 'shop', 'epic', 500),
  avatar('hero', '🦸', 'sky', ['Kahraman', 'Champion'], 'shop', 'epic', 500),
  avatar('siren', '🧜', 'ocean', ['Deniz Sireni', 'Sea Siren'], 'shop', 'epic', 520),
  avatar('mage', '🧙', 'royal', ['Başbüyücü', 'Archmage'], 'shop', 'epic', 550),
  avatar('vampire', '🧛', 'abyss', ['Vampir Lordu', 'Vampire Lord'], 'shop', 'epic', 550),
  avatar('unicorn', '🦄', 'royal', ['Tek Boynuzlu', 'Unicorn'], 'shop', 'epic', 600),
  avatar('prince', '🤴', 'gold', ['Veliaht Prens', 'Crown Prince'], 'shop', 'epic', 650),
  avatar('queen', '👸', 'gold', ['Kraliçe', 'Queen'], 'shop', 'epic', 650),
  avatar('dragon', '🐲', 'ember', ['Ejder', 'Dragon'], 'shop', 'legendary', 900),

  // Gift-box exclusives.
  avatar('ghost', '👻', 'night', ['Hayalet', 'Phantom'], 'box', 'rare'),
  avatar('peacock', '🦚', 'royal', ['Saray Tavusu', 'Royal Peacock'], 'box', 'epic'),
  avatar('flamingo', '🦩', 'blush', ['Flamingo', 'Flamingo'], 'box', 'epic'),
  avatar('genie', '🧞', 'royal', ['Lamba Cini', 'Genie'], 'box', 'epic'),
  avatar('mammoth', '🦣', 'frost', ['Buzul Mamutu', 'Ice Mammoth'], 'box', 'epic'),
  avatar('whale', '🐳', 'ocean', ['Leviathan', 'Leviathan'], 'box', 'legendary'),
  avatar('eagle', '🦅', 'gold', ['Altın Kartal', 'Golden Eagle'], 'box', 'legendary'),
  avatar('elder', '🐉', 'ember', ['Kadim Ejderha', 'Elder Dragon'], 'box', 'legendary'),

  // Frames (CSS: .frame-<suffix>).
  frame('wood', ['Meşe', 'Oak'], 'shop', 'common', 200),
  frame('silver', ['Gümüş', 'Sterling'], 'shop', 'rare', 350),
  frame('ocean', ['Gelgit', 'Tidal'], 'shop', 'rare', 400),
  frame('forest', ['Zümrüt', 'Emerald'], 'shop', 'rare', 400),
  frame('obsidian', ['Obsidyen', 'Obsidian'], 'shop', 'rare', 450),
  frame('gold', ['Altın Varak', 'Gilded'], 'shop', 'epic', 700),
  frame('candy', ['Gül Kuvars', 'Rose Quartz'], 'shop', 'epic', 700),
  frame('frost', ['Buz Kristali', 'Frostbite'], 'shop', 'epic', 750),
  frame('rainbow', ['Ejder Pulu', 'Dragonscale'], 'shop', 'legendary', 1200),
  frame('halo', ['Kutsal Hale', 'Seraph Halo'], 'shop', 'legendary', 1400),
  frame('fire', ['Anka Alevi', 'Phoenix Flame'], 'box', 'epic'),
  frame('thunder', ['Fırtına', 'Stormcaller'], 'box', 'epic'),
  frame('crown', ['Kraliyet Tacı', 'Royal Crown'], 'box', 'legendary'),
  frame('galaxy', ['Nebula', 'Nebula'], 'box', 'legendary'),
  frame('abyss', ['Hiçlik', 'Abyss'], 'box', 'legendary'),

  // Name styles (CSS: .name-<suffix>).
  nameStyle('sky', ['Gök', 'Azure'], 'shop', 'common', 200),
  nameStyle('mint', ['Yeşim', 'Jade'], 'shop', 'common', 200),
  nameStyle('frost', ['Ayaz', 'Frost'], 'shop', 'rare', 350, '❄'),
  nameStyle('sunset', ['Kızıl Şafak', 'Crimson Dawn'], 'shop', 'rare', 400),
  nameStyle('stars', ['Yıldız Tozu', 'Stardust'], 'shop', 'rare', 450, '✦'),
  nameStyle('shadow', ['Gölge', 'Shadow'], 'shop', 'epic', 600),
  nameStyle('sparkle', ['Arkan', 'Arcane'], 'shop', 'epic', 650, '✧'),
  nameStyle('golden', ['Altın Varak', 'Gilded'], 'shop', 'epic', 800),
  nameStyle('rainbow', ['Ejder Ateşi', 'Dragonfire'], 'shop', 'legendary', 1000, '🐉'),
  nameStyle('royal', ['Kraliyet', 'Royal'], 'shop', 'legendary', 1200, '👑'),

  // Added with rewarded ads (2026-11). Items without a price are unlocked by
  // watching ads only (see AD_UNLOCKS); the rest can also be bought with gold.
  avatar('hamster', '🐹', 'sand', ['Hamster', 'Hamster'], 'shop', 'common'),
  avatar('hedgehog', '🦔', 'forest', ['Kirpi', 'Hedgehog'], 'shop', 'common'),
  avatar('crab', '🦀', 'ember', ['Yengeç', 'Crab'], 'shop', 'common'),
  avatar('monkey', '🐵', 'sand', ['Maymun', 'Monkey'], 'shop', 'common', 150),
  avatar('hippo', '🦛', 'stone', ['Su Aygırı', 'Hippo'], 'shop', 'common', 150),
  avatar('sloth', '🦥', 'meadow', ['Tembel Hayvan', 'Sloth'], 'shop', 'common'),
  avatar('otter', '🦦', 'ocean', ['Su Samuru', 'Otter'], 'shop', 'common'),
  avatar('snowman', '⛄', 'frost', ['Kardan Adam', 'Snowman'], 'shop', 'rare'),
  avatar('parrot', '🦜', 'meadow', ['Papağan', 'Parrot'], 'shop', 'rare'),
  avatar('raccoon', '🦝', 'stone', ['Rakun', 'Raccoon'], 'shop', 'rare'),
  avatar('bee', '🐝', 'gold', ['Arı', 'Bee'], 'shop', 'rare'),
  avatar('giraffe', '🦒', 'sand', ['Zürafa', 'Giraffe'], 'shop', 'rare', 250),
  avatar('zebra', '🦓', 'stone', ['Zebra', 'Zebra'], 'shop', 'rare', 250),
  avatar('butterfly', '🦋', 'sky', ['Kelebek', 'Butterfly'], 'shop', 'rare'),
  avatar('scorpion', '🦂', 'ember', ['Akrep', 'Scorpion'], 'shop', 'rare'),
  avatar('kangaroo', '🦘', 'sand', ['Kanguru', 'Kangaroo'], 'shop', 'rare', 280),
  avatar('crocodile', '🐊', 'forest', ['Timsah', 'Crocodile'], 'shop', 'rare', 300),
  avatar('shark', '🦈', 'ocean', ['Köpekbalığı', 'Shark'], 'shop', 'rare'),
  avatar('bat', '🦇', 'night', ['Yarasa', 'Bat'], 'shop', 'rare'),
  avatar('fencer', '🤺', 'sky', ['Kılıç Ustası', 'Fencer'], 'shop', 'rare', 350),
  avatar('rhino', '🦏', 'stone', ['Gergedan', 'Rhino'], 'shop', 'epic', 450),
  avatar('pumpkin', '🎃', 'ember', ['Balkabağı', 'Jack-o-Lantern'], 'shop', 'epic'),
  avatar('zombie', '🧟', 'meadow', ['Zombi', 'Zombie'], 'shop', 'epic'),
  avatar('villain', '🦹', 'abyss', ['Gizemli Kötü', 'Supervillain'], 'shop', 'epic'),
  avatar('astronaut', '🧑‍🚀', 'night', ['Astronot', 'Astronaut'], 'shop', 'epic'),
  avatar('swan', '🦢', 'frost', ['Kuğu', 'Swan'], 'shop', 'epic'),
  avatar('sauropod', '🦕', 'meadow', ['Uzun Boyun', 'Longneck'], 'shop', 'epic'),
  avatar('invader', '👾', 'royal', ['Uzay İstilacısı', 'Space Invader'], 'shop', 'legendary'),

  frame('bubble', ['Köpük', 'Bubble'], 'shop', 'common'),
  frame('mint', ['Nane', 'Mint'], 'shop', 'rare'),
  frame('sunset', ['Gün Batımı', 'Sunset'], 'shop', 'rare', 400),
  frame('lava', ['Lav', 'Lava'], 'shop', 'epic'),
  frame('neon', ['Neon', 'Neon'], 'shop', 'epic', 750),
  frame('aurora', ['Kutup Işıkları', 'Aurora'], 'shop', 'legendary'),

  nameStyle('coral', ['Mercan', 'Coral'], 'shop', 'common', null),
  nameStyle('lime', ['Limon', 'Lime'], 'shop', 'common', null),
  nameStyle('ocean', ['Okyanus', 'Ocean'], 'shop', 'rare', null),
  nameStyle('lava', ['Lav', 'Lava'], 'shop', 'rare', 400),
  nameStyle('neon', ['Neon', 'Neon'], 'shop', 'epic', null, '⚡'),
  nameStyle('aurora', ['Kutup Işığı', 'Aurora'], 'shop', 'legendary', null, '✺'),
];

/**
 * How many rewarded ads unlock an item: about one ad per 50 gold of price, so
 * watching and playing are worth the same. Ad-only items are listed here too.
 */
const AD_UNLOCKS: Record<string, number> = {
  // Gold items that can now also be unlocked with ads.
  'avatar.chick': 3,
  'avatar.turtle': 3,
  'avatar.owl': 5,
  'avatar.dolphin': 6,
  'avatar.robot': 6,
  'avatar.lion': 7,
  'avatar.tiger': 9,
  'avatar.unicorn': 12,
  'avatar.dragon': 18,
  'frame.wood': 4,
  'frame.ocean': 8,
  'frame.gold': 14,
  'frame.rainbow': 24,
  'name.sky': 4,
  'name.mint': 4,
  'name.stars': 9,
  'name.golden': 16,
  // New items.
  'avatar.hamster': 3,
  'avatar.hedgehog': 3,
  'avatar.crab': 3,
  'avatar.monkey': 3,
  'avatar.sloth': 4,
  'avatar.otter': 4,
  'avatar.snowman': 6,
  'avatar.parrot': 6,
  'avatar.raccoon': 6,
  'avatar.bee': 6,
  'avatar.butterfly': 7,
  'avatar.scorpion': 7,
  'avatar.shark': 8,
  'avatar.bat': 8,
  'avatar.rhino': 9,
  'avatar.pumpkin': 12,
  'avatar.zombie': 12,
  'avatar.villain': 12,
  'avatar.astronaut': 14,
  'avatar.swan': 14,
  'avatar.sauropod': 14,
  'avatar.invader': 25,
  'frame.bubble': 4,
  'frame.mint': 6,
  'frame.lava': 14,
  'frame.neon': 15,
  'frame.aurora': 30,
  'name.coral': 4,
  'name.lime': 4,
  'name.ocean': 7,
  'name.neon': 14,
  'name.aurora': 25,
};

export const COSMETICS: readonly Cosmetic[] = CATALOGUE.map((c) => (AD_UNLOCKS[c.id] ? { ...c, ads: AD_UNLOCKS[c.id] } : c));

const BY_ID = new Map(COSMETICS.map((c) => [c.id, c]));

export function cosmetic(id: string | null | undefined): Cosmetic | null {
  return id ? (BY_ID.get(id) ?? null) : null;
}

export const STARTER_AVATARS = COSMETICS.filter((c) => c.kind === 'avatar' && c.source === 'starter');
export const DEFAULT_AVATAR = STARTER_AVATARS[0].id;

/** What a player wears: shown in games, on the leaderboard and on profiles. */
export interface Look {
  avatar: string;
  frame: string | null;
  nameStyle: string | null;
}

export const DEFAULT_LOOK: Look = { avatar: DEFAULT_AVATAR, frame: null, nameStyle: null };

/** Starter items are owned by everyone without being stored. */
export function owns(owned: ReadonlySet<string>, id: string): boolean {
  const item = cosmetic(id);
  return item !== null && (item.source === 'starter' || owned.has(id));
}

/** Drop unknown / unowned items so a stored look always renders. */
export function sanitizeLook(look: Partial<Look> | null | undefined, owned?: ReadonlySet<string>): Look {
  const ok = (id: string | null | undefined, kind: CosmeticKind) => {
    const item = cosmetic(id);
    if (!item || item.kind !== kind) return false;
    return owned ? owns(owned, item.id) : true;
  };
  return {
    avatar: ok(look?.avatar, 'avatar') ? look!.avatar! : DEFAULT_AVATAR,
    frame: ok(look?.frame, 'frame') ? look!.frame! : null,
    nameStyle: ok(look?.nameStyle, 'nameStyle') ? look!.nameStyle! : null,
  };
}

/** CSS suffix for a frame / name style id ("frame.gold" → "gold"). */
export function styleSuffix(id: string): string {
  return id.slice(id.indexOf('.') + 1);
}
